// auth/AuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import ApiService from "../../services/ApiService";

const AuthContext = createContext(null);

// Helper: convert API "me" into your LandmarkEmployee-like object
function toEmployee(user) {
  if (!user) return null;

  const email = user.email || "";
  const full = user.fullName || "";
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const firstName =
    parts[0] || (email.includes("@") ? email.split("@")[0] : email) || "";
  const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";

  return {
    // keep close to your LandmarkEmployee schema
    employeeId: user.id,
    email,
    emailLowercase: email.toLowerCase(),
    firstName,
    lastName,
    fullName: full || `${firstName}${lastName ? " " + lastName : ""}`,
    // optional fields you might use elsewhere
    lastSeenTime: undefined,
    createdTime: undefined,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // raw API shape from /auth/me
  const [loading, setLoading] = useState(true);

  const refreshAuth = useCallback(async () => {
    try {
      const api = new ApiService();
      const me = await api.get("landmark/employees/auth/me");
      if (me && me.success) {
        setUser({
          id: me.id,
          email: me.email,
          fullName: me.fullName,
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log("[AuthProvider] (LandmarkAuthContext) mounting - refreshAuth()");
    refreshAuth();
  }, [refreshAuth]);

  const logout = useCallback(async () => {
    try {
      const api = new ApiService();
      await api.post("landmark/employees/auth/logout", {});
    } finally {
      setUser(null);
    }
  }, []);

  const employee = useMemo(() => toEmployee(user), [user]);
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{ user, employee, isAuthenticated, loading, refreshAuth, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Optional convenience hook
export function useEmployee() {
  const { employee } = useAuth();
  return employee;
}
